import React, { useState } from 'react'
import styled from 'styled-components'
import { MenuProps, message } from 'antd'
import { CloseOutlined, PaperClipOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { NAMESPACE } from 'locale'
import { deviceRecordConstants } from 'locale/deviceRecords'
import {
  EmptyState,
  Spinner,
  NavigationModal,
  Heading,
  ExpandableCard,
  ExpandableCardField
} from '@lmc/ui-kit'
import { DeviceRecord } from '../types'
import { formatDate, formatDateTime } from '../utils/deviceRecordUtils'
import { useDeviceRecordArchive } from '../hooks/useDeviceRecordArchive'
import { useDeviceRecordDocumentRemove } from '../hooks/useDeviceRecordDocumentRemove'
import useRoleAccess from '../../../../../components/AccessControl/useRoleAccess'
import { UserRole } from '@lmc/ts-common'
import { useInfiniteScrollFetch } from '../../../../../hooks/useInfiniteScroll'

interface DeviceRecordListProps {
  deviceRecords: DeviceRecord[]
  isLoading: boolean
  onEdit: (deviceRecord: DeviceRecord) => void
  onRefetch: () => void
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

const STATUS_TAG_COLORS: Record<string, string> = {
  active: 'green',
  archived: 'default'
}

export const DeviceRecordList: React.FC<DeviceRecordListProps> = ({
  deviceRecords,
  isLoading,
  onEdit,
  onRefetch,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage
}) => {
  const { t } = useTranslation(NAMESPACE.DEVICE_RECORDS)
  const { id: residentId } = useParams<{ id: string }>()
  const [expandedDeviceRecordId, setExpandedDeviceRecordId] = useState<
    string | null
  >(null)
  const [archiveModalVisible, setArchiveModalVisible] = useState(false)
  const [selectedDeviceRecord, setSelectedDeviceRecord] =
    useState<DeviceRecord | null>(null)
  const [showRemoveDocModal, setShowRemoveDocModal] = useState(false)
  const [docToRemove, setDocToRemove] = useState<{
    recordId: string
    documentId: string
    filename: string
  } | null>(null)

  const { archiveDeviceRecord, unarchiveDeviceRecord } = useDeviceRecordArchive(
    residentId || '',
    () => {
      setArchiveModalVisible(false)
      setSelectedDeviceRecord(null)
    }
  )
  const { removeDocument } = useDeviceRecordDocumentRemove({
    residentId: residentId || ''
  })

  const { infiniteRef, rootRef } = useInfiniteScrollFetch({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  })

  const hasEditAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'DeviceRecord'
      }
    ]
  )

  const hasArchiveAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin],
    [
      {
        i: 'delete',
        this: 'DeviceRecord'
      }
    ]
  )

  if (isLoading) {
    return <Spinner />
  }

  if (deviceRecords.length === 0) {
    return (
      <EmptyState
        title={t(deviceRecordConstants.NO_DEVICE_RECORDS_FOUND)}
        description={t(deviceRecordConstants.NO_DEVICE_RECORDS_DESCRIPTION)}
      />
    )
  }

  const activeDeviceRecords = deviceRecords.filter(
    (record) => !record.archivedAt
  )
  const archivedDeviceRecords = deviceRecords.filter((record) =>
    Boolean(record.archivedAt)
  )

  const toggleExpand = (deviceRecordId: string) => {
    setExpandedDeviceRecordId(
      expandedDeviceRecordId === deviceRecordId ? null : deviceRecordId
    )
  }

  const handleArchiveClick = (deviceRecord: DeviceRecord) => {
    setSelectedDeviceRecord(deviceRecord)
    setArchiveModalVisible(true)
  }

  const handleConfirmArchive = async () => {
    if (!selectedDeviceRecord) return

    try {
      if (selectedDeviceRecord.archivedAt) {
        await unarchiveDeviceRecord(selectedDeviceRecord.id)
      } else {
        await archiveDeviceRecord(selectedDeviceRecord.id)
      }
    } catch (error) {
      console.error('Failed to archive/unarchive device record:', error)
    }
  }

  const getMenuItems = (deviceRecord: DeviceRecord): MenuProps['items'] => {
    const items: MenuProps['items'] = []

    if (hasEditAccess && !deviceRecord.archivedAt) {
      items.push({
        key: 'edit',
        label: t(deviceRecordConstants.EDIT),
        onClick: () => onEdit(deviceRecord)
      })
    }

    if (hasArchiveAccess) {
      items.push({
        key: 'archive',
        label: deviceRecord.archivedAt
          ? t(deviceRecordConstants.UN_ARCHIVE)
          : t(deviceRecordConstants.ARCHIVE),
        onClick: () => handleArchiveClick(deviceRecord)
      })
    }

    return items
  }

  const getStatusLabel = (deviceRecord: DeviceRecord) => {
    return deviceRecord.archivedAt
      ? t(deviceRecordConstants.ARCHIVED)
      : t(deviceRecordConstants.ACTIVE)
  }

  const isInUse = (deviceRecord: DeviceRecord) =>
    Boolean(
      deviceRecord.startDate &&
        new Date(deviceRecord.startDate) < new Date() &&
        (!deviceRecord.endDate || new Date(deviceRecord.endDate) > new Date())
    )

  const getActorName = (actor?: DeviceRecord['createdBy']) => {
    if (!actor) return ''
    if (typeof actor === 'string') return actor

    const first = actor.name?.first || ''
    const last = actor.name?.last || ''
    const fullName = `${first} ${last}`.trim()

    return fullName || actor.id
  }

  const buildDateRangeText = (deviceRecord: DeviceRecord) => {
    const start = formatDate(deviceRecord.startDate)
    const end = formatDate(deviceRecord.endDate)

    if (start && end) {
      return `${start} - ${end}`
    }

    return start || end || t(deviceRecordConstants.NO_DATE_TIME_SPECIFIED)
  }

  const buildFields = (deviceRecord: DeviceRecord): ExpandableCardField[] => {
    const fields: ExpandableCardField[] = []

    if (deviceRecord.location) {
      fields.push({
        label: t(deviceRecordConstants.LOCATION),
        value: deviceRecord.location,
        fullWidth: true
      })
    }

    if (deviceRecord.description) {
      fields.push({
        label: t(deviceRecordConstants.DESCRIPTION),
        value: deviceRecord.description,
        fullWidth: true
      })
    }

    if (deviceRecord.documents && deviceRecord.documents.length > 0) {
      const documentLinks = deviceRecord.documents.map((doc, index) => {
        const docId = doc.id
        const filename =
          doc.filename ||
          t(deviceRecordConstants.DOCUMENT_NUMBER, { number: index + 1 })
        const url = doc.url

        if (!url) {
          return (
            <span key={`${deviceRecord.id}-${docId || index}`}>{filename}</span>
          )
        }

        return (
          <DocumentRow key={`${deviceRecord.id}-${docId || index}`}>
            <DocumentLink href={url} target="_blank" rel="noopener noreferrer">
              {filename}
            </DocumentLink>
            {hasEditAccess && !deviceRecord.archivedAt && docId && (
              <RemoveButton
                onClick={(e) => {
                  e.stopPropagation()
                  setDocToRemove({
                    recordId: deviceRecord.id,
                    documentId: docId,
                    filename
                  })
                  setShowRemoveDocModal(true)
                }}
                title={t(deviceRecordConstants.REMOVE_DOCUMENT)}
              >
                <CloseOutlined />
              </RemoveButton>
            )}
          </DocumentRow>
        )
      })

      fields.push({
        label: t(deviceRecordConstants.DOCUMENTS),
        value: <DocumentLinksContainer>{documentLinks}</DocumentLinksContainer>,
        fullWidth: true
      })
    }

    if (deviceRecord.createdBy) {
      const createdByName = getActorName(deviceRecord.createdBy)
      fields.push({
        label: t(deviceRecordConstants.CREATED_BY),
        value: `${createdByName}${
          deviceRecord.createdAt
            ? ` ${t(deviceRecordConstants.ON)} ${formatDateTime(deviceRecord.createdAt)}`
            : ''
        }`
      })
    }

    if (deviceRecord.updatedBy) {
      const updatedByName = getActorName(deviceRecord.updatedBy)
      fields.push({
        label: t(deviceRecordConstants.UPDATED_BY),
        value: `${updatedByName}${
          deviceRecord.updatedAt
            ? ` ${t(deviceRecordConstants.ON)} ${formatDateTime(deviceRecord.updatedAt)}`
            : ''
        }`
      })
    }

    return fields
  }

  const renderDeviceRecord = (deviceRecord: DeviceRecord) => {
    const menuItems = getMenuItems(deviceRecord)
    const fields = buildFields(deviceRecord)
    const status = deviceRecord.archivedAt
      ? {
          label: getStatusLabel(deviceRecord),
          color: STATUS_TAG_COLORS.archived
        }
      : isInUse(deviceRecord)
        ? {
            label: t(deviceRecordConstants.IN_USE),
            color: STATUS_TAG_COLORS.active
          }
        : undefined

    return (
      <ExpandableCard
        key={deviceRecord.id}
        id={deviceRecord.id}
        title={deviceRecord.name}
        status={status}
        date={`${t(deviceRecordConstants.DEVICE_PERIOD)}: ${buildDateRangeText(deviceRecord)}`}
        titleIndicators={
          deviceRecord.documents && deviceRecord.documents.length > 0 ? (
            <AttachmentIcon title={t(deviceRecordConstants.HAS_ATTACHMENT)}>
              <PaperClipOutlined />
              {deviceRecord.documents.length > 1 && (
                <span style={{ marginLeft: '4px' }}>
                  ({deviceRecord.documents.length})
                </span>
              )}
            </AttachmentIcon>
          ) : undefined
        }
        fields={fields}
        menuItems={menuItems}
        isExpanded={expandedDeviceRecordId === deviceRecord.id}
        onToggleExpand={fields.length > 0 ? toggleExpand : undefined}
        testId={`device-record-${deviceRecord.id}`}
      />
    )
  }

  return (
    <>
      <ScrollContainer ref={rootRef}>
        <ListContainer>
          {activeDeviceRecords.map(renderDeviceRecord)}

          {archivedDeviceRecords.length > 0 && (
            <>
              <SectionHeading>
                <Heading level={4}>
                  {t(deviceRecordConstants.ARCHIVED_DEVICE_RECORDS)}
                </Heading>
              </SectionHeading>
              {archivedDeviceRecords.map(renderDeviceRecord)}
            </>
          )}

          {(hasNextPage || isFetchingNextPage) && (
            <LoadMoreContainer ref={infiniteRef}>
              {isFetchingNextPage && <Spinner />}
            </LoadMoreContainer>
          )}
        </ListContainer>
      </ScrollContainer>

      <NavigationModal
        showPrompt={archiveModalVisible}
        confirmNavigation={handleConfirmArchive}
        cancelNavigation={() => {
          setArchiveModalVisible(false)
          setSelectedDeviceRecord(null)
        }}
        modalTitle={
          selectedDeviceRecord?.archivedAt
            ? t(deviceRecordConstants.RESTORE_DEVICE_RECORD)
            : t(deviceRecordConstants.ARCHIVE_DEVICE_RECORD)
        }
        modalContentText={
          selectedDeviceRecord?.archivedAt
            ? t(deviceRecordConstants.RESTORE_CONFIRMATION)
            : t(deviceRecordConstants.ARCHIVE_CONFIRMATION)
        }
        modalConfirmText={
          selectedDeviceRecord?.archivedAt
            ? t(deviceRecordConstants.RESTORE)
            : t(deviceRecordConstants.ARCHIVE)
        }
        modalCancelText={t(deviceRecordConstants.CANCEL)}
      />
      <NavigationModal
        showPrompt={showRemoveDocModal}
        modalTitle={t(deviceRecordConstants.REMOVE_DOCUMENT)}
        modalContentText={t(
          deviceRecordConstants.REMOVE_DOCUMENT_CONFIRMATION,
          {
            filename: docToRemove?.filename
          }
        )}
        modalConfirmText={t(deviceRecordConstants.REMOVE)}
        modalCancelText={t(deviceRecordConstants.CANCEL)}
        confirmNavigation={async () => {
          if (!docToRemove) return

          const latestRecord = deviceRecords.find(
            (record) => record.id === docToRemove.recordId
          )
          const latestDocumentIds =
            latestRecord?.documents?.map((document) => document.id) || []
          const docIdToRemove =
            docToRemove.documentId as (typeof latestDocumentIds)[number]

          if (!latestDocumentIds.includes(docIdToRemove)) {
            message.warning(
              t(deviceRecordConstants.DOCUMENT_NO_LONGER_AVAILABLE)
            )
            setShowRemoveDocModal(false)
            setDocToRemove(null)
            onRefetch()
            return
          }

          const updatedDocumentIds = latestDocumentIds.filter(
            (docId) => docId !== docIdToRemove
          )

          try {
            await removeDocument(docToRemove.recordId, updatedDocumentIds)
            setShowRemoveDocModal(false)
            setDocToRemove(null)
          } catch (error) {
            console.error('Failed to remove document:', error)
          }
        }}
        cancelNavigation={() => {
          setShowRemoveDocModal(false)
          setDocToRemove(null)
        }}
      />
    </>
  )
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px auto 0px auto;
  padding-bottom: 40px;
  max-width: 1080px;
`

const SectionHeading = styled.div`
  margin-top: 32px;
  margin-bottom: 16px;
`

const ScrollContainer = styled.div`
  overflow-y: auto;
  height: 100%;
`

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`

const AttachmentIcon = styled.span`
  color: ${({ theme }) => theme.color?.status?.popout || '#FF5887'};
  font-size: 14px;
`

const DocumentLinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DocumentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const DocumentLink = styled.a`
  font-size: 14px;
  color: ${({ theme }) => theme.color?.status?.popout || '#FF5887'};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color?.neutral?.color5 || '#8c8c8c'};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.color?.status?.danger || '#ff4d4f'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
